import {
  TriggerDueDateArrivedInfo,
  TriggerItemRequestSubmittedInfo,
  TriggerTimeFrequencyInfo,
  WorkflowUIConditionProperties,
  WorkflowCycleOptions,
} from '@/models/workflow.models';
import { defineStore } from 'pinia';

interface WorkflowInputParamsWithUI {
  ui?: {
    triggerField?: string;
  };
}
import { useOrganizationStore } from './organizationStore';
import {
  getOptionalOperatorFromTriggerConditions,
  translatedNestedConditions,
  useAutomations,
} from '@/utils/automations';
import cloneDeep from 'lodash-es/cloneDeep';
import {
  Colors,
  UpdateWorkflow,
  UpdateWorkflowGroup,
  Workflow,
  WorkflowAnyConditions,
  WorkflowConditionProperties,
  CreateWorkflow,
  CreateWorkflowGroup,
  FindWorkflowsData,
  FindWorkflowRunsData,
  WorkflowGroup,
  WorkflowNestedCondition,
  WorkflowOperator,
  WorkflowRunListResponse,
  WorkflowTrigger,
  WorkflowService,
  FindWorkflowsResponse,
} from '@/api';

const { extractTriggerTimeFrequencyInfo, extractDueDateArrivedInfo } =
  useAutomations();

export interface WorkflowStateInterface {
  dialogWorkflow: Workflow | CreateWorkflow | null;
  automationGroups: Array<WorkflowGroup>;
}

export const useWorkflowStore = defineStore('workflow', {
  /****
   STATE
   ****/
  state: (): WorkflowStateInterface => ({
    dialogWorkflow: initialCreateWorkflowState() as CreateWorkflow,
    automationGroups: [],
  }),

  /****
   GETTERS
   ****/
  getters: {
    uiTrigger():
      | { value: WorkflowTrigger; field?: string; operator?: WorkflowOperator }
      | undefined {
      if (!this.dialogWorkflow?.trigger) {
        return undefined;
      }
      if (
        this.dialogWorkflow.trigger === WorkflowTrigger.FOLDER_UPDATED ||
        this.dialogWorkflow.trigger === WorkflowTrigger.ITEM_UPDATED
      ) {
        const operator: WorkflowOperator | undefined =
          getOptionalOperatorFromTriggerConditions(
            this.dialogWorkflow.triggerConditions
          );

        return {
          value: this.dialogWorkflow.trigger,
          field: (this.dialogWorkflow.inputParams as WorkflowInputParamsWithUI)
            ?.ui?.triggerField,
          operator,
        };
      }
      return { value: this.dialogWorkflow.trigger };
    },

    uiTriggerInvalid(): boolean {
      if (!this.dialogWorkflow?.trigger) return true;
      // if we are updating a folder or item, we need to have a trigger field
      if (
        this.dialogWorkflow.trigger === WorkflowTrigger.FOLDER_UPDATED ||
        this.dialogWorkflow.trigger === WorkflowTrigger.ITEM_UPDATED
      ) {
        return !(this.dialogWorkflow.inputParams as WorkflowInputParamsWithUI)
          ?.ui?.triggerField;
      }
      return false;
    },

    triggerTimeFrequencyInfo(): TriggerTimeFrequencyInfo | undefined {
      return extractTriggerTimeFrequencyInfo(this.dialogWorkflow || undefined);
    },

    triggerDueDateArrivedInfo(): TriggerDueDateArrivedInfo | undefined {
      return extractDueDateArrivedInfo(this.dialogWorkflow || undefined);
    },

    triggerParentNumberInfo():
      | {
          beforeParentNumbers: Array<string>;
          afterParentNumbers: Array<string>;
        }
      | undefined {
      if (
        this.dialogWorkflow?.trigger !== WorkflowTrigger.ITEM_UPDATED &&
        this.dialogWorkflow?.trigger !== WorkflowTrigger.FOLDER_UPDATED
      ) {
        return undefined;
      }
      const { before, after } = extractTriggerBeforeAndAfterValues(
        this.dialogWorkflow.triggerConditions,
        'parentNumber'
      );
      return {
        beforeParentNumbers: before as string[],
        afterParentNumbers: after as string[],
      };
    },

    triggerColorInfo():
      | {
          beforeColors: Array<Colors>;
          afterColors: Array<Colors>;
        }
      | undefined {
      if (
        this.dialogWorkflow?.trigger !== WorkflowTrigger.ITEM_UPDATED &&
        this.dialogWorkflow?.trigger !== WorkflowTrigger.FOLDER_UPDATED
      ) {
        return undefined;
      }
      const { before, after } = extractTriggerBeforeAndAfterValues(
        this.dialogWorkflow.triggerConditions,
        'color'
      );
      return {
        beforeColors: before as Array<Colors>,
        afterColors: after as Array<Colors>,
      };
    },

    triggerLaneInfo():
      | {
          beforeLaneIds: Array<string>;
          afterLaneIds: Array<string>;
        }
      | undefined {
      if (this.dialogWorkflow?.trigger !== WorkflowTrigger.ITEM_UPDATED) {
        return undefined;
      }
      const { before, after } = extractTriggerBeforeAndAfterValues(
        this.dialogWorkflow.triggerConditions,
        'laneId'
      );
      return {
        beforeLaneIds: before as string[],
        afterLaneIds: after as string[],
      };
    },

    triggerPriorityInfo():
      | {
          beforePriorityIds: Array<string>;
          afterPriorityIds: Array<string>;
        }
      | undefined {
      if (this.dialogWorkflow?.trigger !== WorkflowTrigger.ITEM_UPDATED) {
        return undefined;
      }
      const { before, after } = extractTriggerBeforeAndAfterValues(
        this.dialogWorkflow.triggerConditions,
        'priorityId'
      );
      return {
        beforePriorityIds: before as string[],
        afterPriorityIds: after as string[],
      };
    },

    triggerWorkTypeInfo():
      | {
          beforeWorkTypeIds: Array<string>;
          afterWorkTypeIds: Array<string>;
        }
      | undefined {
      if (this.dialogWorkflow?.trigger !== WorkflowTrigger.ITEM_UPDATED) {
        return undefined;
      }
      const { before, after } = extractTriggerBeforeAndAfterValues(
        this.dialogWorkflow.triggerConditions,
        'workTypeId'
      );
      return {
        beforeWorkTypeIds: before as string[],
        afterWorkTypeIds: after as string[],
      };
    },

    triggerTeamInfo():
      | {
          beforeTeamIds: Array<string | null>;
          afterTeamIds: Array<string | null>;
        }
      | undefined {
      if (this.dialogWorkflow?.trigger !== WorkflowTrigger.ITEM_UPDATED) {
        return undefined;
      }
      const { before, after } = extractTriggerBeforeAndAfterValues(
        this.dialogWorkflow.triggerConditions,
        'teamId'
      );
      return {
        beforeTeamIds: before as Array<string | null>,
        afterTeamIds: after as Array<string | null>,
      };
    },

    triggerAssigneeInfo():
      | {
          beforeAssigneeIds: Array<string | null>;
          afterAssigneeIds: Array<string | null>;
        }
      | undefined {
      if (this.dialogWorkflow?.trigger !== WorkflowTrigger.ITEM_UPDATED) {
        return undefined;
      }
      const { before, after } = extractTriggerBeforeAndAfterValues(
        this.dialogWorkflow.triggerConditions,
        'assigneeUsername'
      );
      return {
        beforeAssigneeIds: before as Array<string | null>,
        afterAssigneeIds: after as Array<string | null>,
      };
    },

    triggerCycleInfo():
      | {
          beforeCycleOptions: Array<WorkflowCycleOptions | null>;
          afterCycleOptions: Array<WorkflowCycleOptions | null>;
        }
      | undefined {
      if (this.dialogWorkflow?.trigger !== WorkflowTrigger.ITEM_UPDATED) {
        return undefined;
      }
      const { before, after } = extractTriggerBeforeAndAfterValues(
        this.dialogWorkflow.triggerConditions,
        'boardType'
      );
      return {
        beforeCycleOptions: before as Array<WorkflowCycleOptions | null>,
        afterCycleOptions: after as Array<WorkflowCycleOptions | null>,
      };
    },

    triggerBoardInfo():
      | {
          beforeBoardIds: Array<string>;
          afterBoardIds: Array<string>;
        }
      | undefined {
      if (this.dialogWorkflow?.trigger !== WorkflowTrigger.ITEM_UPDATED) {
        return undefined;
      }
      const { before, after } = extractTriggerBeforeAndAfterValues(
        this.dialogWorkflow.triggerConditions,
        'boardId'
      );
      return {
        beforeBoardIds: before as string[],
        afterBoardIds: after as string[],
      };
    },

    triggerLabelInfo():
      | {
          beforeLabelIds: Array<string>;
          afterLabelIds: Array<string>;
        }
      | undefined {
      if (this.dialogWorkflow?.trigger !== WorkflowTrigger.ITEM_UPDATED) {
        return undefined;
      }
      const { before, after } = extractTriggerBeforeAndAfterValues(
        this.dialogWorkflow.triggerConditions,
        'labels'
      );
      return {
        beforeLabelIds: before as string[],
        afterLabelIds: after as string[],
      };
    },

    triggerItemRequestInfo(): TriggerItemRequestSubmittedInfo | undefined {
      if (
        this.dialogWorkflow?.trigger !== WorkflowTrigger.ITEM_REQUEST_SUBMITTED
      )
        return undefined;
      const { selectedValues } = extractTriggerSelectedValues(
        this.dialogWorkflow.triggerConditions,
        'itemRequest.id'
      );
      return {
        itemRequestIds: (selectedValues as string[][]).flat(),
      };
    },

    getTriggerCustomFieldInfo: (state) => {
      // NOTE: returning a function here since we need to pass in the custom field id
      return (
        customFieldId: string
      ):
        | {
            beforeCustomFieldValues: Array<string | null>;
            afterCustomFieldValues: Array<string | null>;
          }
        | undefined => {
        if (state.dialogWorkflow?.trigger !== WorkflowTrigger.ITEM_UPDATED) {
          return undefined;
        }
        const { before, after } = extractTriggerBeforeAndAfterValues(
          state.dialogWorkflow.triggerConditions,
          `customFields.${customFieldId}`
        );
        return {
          beforeCustomFieldValues: before as Array<string | null>,
          afterCustomFieldValues: after as Array<string | null>,
        };
      };
    },

    triggerCustomField: (state) => {
      if (
        state.dialogWorkflow?.trigger !== WorkflowTrigger.ITEM_UPDATED ||
        (state.dialogWorkflow?.inputParams as WorkflowInputParamsWithUI)?.ui
          ?.triggerField !== 'customFields'
      ) {
        return undefined;
      }
      const fieldId = extractTriggerCustomFieldId(
        state.dialogWorkflow.triggerConditions
      );
      if (!fieldId) return undefined;
      const organizationStore = useOrganizationStore();
      const activeOrganization = organizationStore.activeOrganization;
      if (!activeOrganization) return;
      const customField = activeOrganization.customFields.find(
        (cf) => cf.id === fieldId
      );
      return customField;
    },

    translatedNestedConditions(): Array<WorkflowUIConditionProperties> {
      if (!this.dialogWorkflow) return [];
      return translatedNestedConditions(this.dialogWorkflow);
    },
  },

  /****
   ACTIONS
   ****/
  actions: {
    resetDialogWorkflow() {
      this.dialogWorkflow = initialCreateWorkflowState() as CreateWorkflow;
    },

    setDialogWorkflow(workflow: Workflow | CreateWorkflow) {
      this.dialogWorkflow = cloneDeep(workflow);
    },

    setNestedConditionsFromUIConditions(
      conditions: Array<WorkflowUIConditionProperties>
    ) {
      if (!this.dialogWorkflow?.trigger) return;
      const newNestedConditions: Array<WorkflowNestedCondition> = [];
      const isAfterConditions =
        this.dialogWorkflow.trigger === WorkflowTrigger.ITEM_UPDATED ||
        this.dialogWorkflow.trigger === WorkflowTrigger.FOLDER_UPDATED;
      const pathPrefix = isAfterConditions ? '$.after' : '$';
      for (const condition of conditions) {
        const conditionNeedsValue = !['isSet', 'notSet'].includes(
          condition.operator
        );
        if (
          conditionNeedsValue &&
          Array.isArray(condition.value) &&
          !condition.value.length
        )
          continue;
        if (!conditionNeedsValue) {
          newNestedConditions.push({
            path: `${pathPrefix}.${condition.field}`,
            operator: condition.operator,
          });
        } else if (
          !Array.isArray(condition.value) ||
          condition.value.length === 1
        ) {
          newNestedConditions.push({
            path: `${pathPrefix}.${condition.field}`,
            operator: condition.operator,
            value: Array.isArray(condition.value)
              ? condition.value[0]
              : condition.value,
          });
        } else {
          if (
            condition.operator === 'notEqual' ||
            condition.operator === 'doesNotContain'
          ) {
            newNestedConditions.push({
              all: condition.value.map((value) => ({
                path: `${pathPrefix}.${condition.field}`,
                operator: condition.operator,
                value,
              })),
            });
          } else {
            newNestedConditions.push({
              any: condition.value.map((value) => ({
                path: `${pathPrefix}.${condition.field}`,
                operator: condition.operator,
                value,
              })),
            });
          }
        }
      }
      this.dialogWorkflow.nestedConditions = newNestedConditions;
    },

    async createWorkflow(description?: string, workflowGroupId?: string) {
      if (!this.dialogWorkflow) return;
      const organizationStore = useOrganizationStore();
      const activeOrganization = organizationStore.activeOrganization;
      if (!activeOrganization) return;
      const response = await WorkflowService.createWorkflow<true>({
        path: { organizationId: activeOrganization.id },
        body: {
          ...this.dialogWorkflow,
          workflowGroupId,
          description,
        },
      });
      return response.data.data;
    },

    async findWorkflows(
      query: FindWorkflowsData['query']
    ): Promise<FindWorkflowsResponse | undefined> {
      const organizationStore = useOrganizationStore();
      const activeOrganization = organizationStore.activeOrganization;
      if (!activeOrganization) return;
      const response = await WorkflowService.findWorkflows<true>({
        path: { organizationId: activeOrganization.id },
        query,
      });
      return response.data;
    },

    async updateWorkflow(
      workflowId: string,
      workflow: UpdateWorkflow
    ): Promise<Workflow | undefined> {
      const organizationStore = useOrganizationStore();
      const activeOrganization = organizationStore.activeOrganization;
      if (!activeOrganization) return;

      const response = await WorkflowService.updateWorkflow<true>({
        path: { organizationId: activeOrganization.id, workflowId },
        body: workflow,
      });

      return response.data.data;
    },

    async deleteWorkflow(workflowId: string): Promise<void> {
      const organizationStore = useOrganizationStore();
      const activeOrganization = organizationStore.activeOrganization;
      if (!activeOrganization) return;
      await WorkflowService.deleteWorkflow<true>({
        path: { organizationId: activeOrganization.id, workflowId },
      });
    },

    async findWorkflowRuns(
      query: FindWorkflowRunsData['query']
    ): Promise<WorkflowRunListResponse | undefined> {
      const organizationStore = useOrganizationStore();
      const activeOrganization = organizationStore.activeOrganization;
      if (!activeOrganization) return;
      const response = await WorkflowService.findWorkflowRuns<true>({
        path: { organizationId: activeOrganization.id },
        query,
      });
      return response.data;
    },
    async findWorkflowGroups() {
      const organizationStore = useOrganizationStore();
      const activeOrganization = organizationStore.activeOrganization;
      if (!activeOrganization) return;
      const response = await WorkflowService.findWorkflowGroups<true>({
        path: { organizationId: activeOrganization.id },
      });
      if (response) {
        this.automationGroups = response.data.data;
      }
    },

    async createWorkFlowGroup(createWorkFlowGroup: CreateWorkflowGroup) {
      const organizationStore = useOrganizationStore();
      const activeOrganization = organizationStore.activeOrganization;
      if (!activeOrganization) return;
      const response = await WorkflowService.createWorkflowGroup<true>({
        path: { organizationId: activeOrganization.id },
        body: createWorkFlowGroup,
      });
      if (response) {
        this.automationGroups.push(response.data.data);
      }
      return response;
    },

    async updateWorkflowGroup(
      workflowGroupId: string,
      updateWorkflowGroup: UpdateWorkflowGroup
    ) {
      const organizationStore = useOrganizationStore();
      const activeOrganization = organizationStore.activeOrganization;
      if (!activeOrganization) return;
      const response = await WorkflowService.updateWorkflowGroup<true>({
        path: { organizationId: activeOrganization.id, workflowGroupId },
        body: updateWorkflowGroup,
      });
      if (response) {
        const index = this.automationGroups.findIndex(
          (group) => group.id === workflowGroupId
        );
        if (index !== -1) {
          this.automationGroups[index] = response.data.data;
        }
      }
      return response;
    },

    async deleteWorkflowGroup(workflowGroupId: string) {
      const organizationStore = useOrganizationStore();
      const activeOrganization = organizationStore.activeOrganization;
      if (!activeOrganization) return;
      await WorkflowService.deleteWorkflowGroup<true>({
        path: { organizationId: activeOrganization.id, workflowGroupId },
      });
      this.automationGroups = this.automationGroups.filter(
        (group) => group.id !== workflowGroupId
      );
    },
  },
});

function initialCreateWorkflowState(): Partial<CreateWorkflow> {
  return {
    actions: [],
    triggerConditions: [],
    nestedConditions: [],
    inputParams: {},
  };
}

function extractTriggerBeforeAndAfterValues(
  conditions: Array<WorkflowNestedCondition> | undefined,
  attribute: string
): {
  before: Array<unknown>;
  after: Array<unknown>;
} {
  const relevantConditions: Array<WorkflowConditionProperties> = [];

  conditions?.forEach((condition) => {
    if (
      (condition as WorkflowConditionProperties).path ===
        `$.before.${attribute}` ||
      (condition as WorkflowConditionProperties).path === `$.after.${attribute}`
    ) {
      relevantConditions.push(condition as WorkflowConditionProperties);
    }
    if ((condition as WorkflowAnyConditions).any) {
      (condition as WorkflowAnyConditions).any.forEach((anyCondition) => {
        if (
          (anyCondition as WorkflowConditionProperties).path ===
            `$.before.${attribute}` ||
          (anyCondition as WorkflowConditionProperties).path ===
            `$.after.${attribute}`
        ) {
          relevantConditions.push(anyCondition as WorkflowConditionProperties);
        }
      });
    }
  });

  return {
    before: relevantConditions
      .filter((c) => c.path === `$.before.${attribute}`)
      .map((c) => c.value),
    after: relevantConditions
      .filter((c) => c.path === `$.after.${attribute}`)
      .map((c) => c.value),
  };
}

function extractTriggerSelectedValues(
  conditions: Array<WorkflowNestedCondition> | undefined,
  attribute: string
): {
  selectedValues: Array<unknown>;
} {
  const selectedValues: Array<unknown> = [];
  conditions?.forEach((condition) => {
    if ((condition as WorkflowConditionProperties).path === `$.${attribute}`) {
      selectedValues.push((condition as WorkflowConditionProperties).value);
    }
  });
  return { selectedValues };
}

function extractTriggerCustomFieldId(
  conditions: Array<WorkflowNestedCondition> | undefined
): string | undefined {
  const pathsNotEqualCondition = conditions?.find(
    (c) => (c as WorkflowConditionProperties).operator === 'pathsNotEqual'
  );
  if (!pathsNotEqualCondition) return undefined;
  const path = (
    (pathsNotEqualCondition as WorkflowConditionProperties).value as Array<
      string | undefined
    >
  )[0];
  if (!path) return undefined;
  const pathParts = path.split('.');
  if (pathParts.length < 4) return undefined;
  return pathParts[3];
}
